/**
 * Hardware Placement Engine
 *
 * Ported from Cabinet_Estimator/viewer_3d.py (lines 3627-4835).
 * Computes 3D positions for hardware items on cabinet surfaces using
 * a 3-axis alignment system with mirror/pattern post-processing.
 */

import type {
  Vec3,
  HardwarePlacement,
  MountingProfileData,
  CabinetDimensions,
} from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Standard panel thickness (inches) */
const DEFAULT_PT = 0.75;
/** Back panel thickness (inches) */
const DEFAULT_BT = 0.25;
/** Degrees-to-radians conversion factor */
const DEG2RAD = Math.PI / 180;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface PanelDims {
  w: number;
  h: number;
  t: number;
  isDoor: boolean;
}

/**
 * Parse mount_surface string into a panelType and mountFace.
 * Accepts formats like "Door Panel", "Side Panel:interior_left",
 * "Back Panel:front", etc.
 */
function resolveMountSurface(profile: MountingProfileData): {
  panelType: string;
  mountFace: string;
} {
  const surface = profile.mount_surface || 'Door Panel';
  const face = profile.mount_face || 'front';
  return { panelType: surface, mountFace: face };
}

/**
 * Evenly distribute `distQty` positions along a panel's height axis.
 * Returns Y positions spanning from an inset margin to panelH minus that margin.
 */
function computeDistributeYPositions(
  baseY: number,
  panelH: number,
  distQty: number,
): number[] {
  if (distQty <= 1) return [baseY];
  const margin = Math.min(baseY, panelH - baseY);
  const startY = margin;
  const endY = panelH - margin;
  const positions: number[] = [];
  for (let d = 0; d < distQty; d++) {
    const t = d / (distQty - 1);
    positions.push(startY + t * (endY - startY));
  }
  return positions;
}

// ---------------------------------------------------------------------------
// computeAlignmentBase
// ---------------------------------------------------------------------------

/**
 * Compute the base position on a panel surface using 3-axis alignment.
 *
 * Returns `{x, y, z}` in the panel's local coordinate system where:
 *   - x: 0 = left edge of panel, panelW = right edge
 *   - y: 0 = bottom of panel, panelH = top
 *   - z: 0 = back face, panelT = front face
 *
 * Perspective convention:
 *   - For doors: front = exterior face (facing viewer), back = interior (facing cabinet)
 *   - For carcass panels: front = interior face (facing cabinet interior), back = exterior
 *
 * @param alignX  Horizontal alignment: 'left'|'right'|'middle'|'hinge_side'|'hinge'|'open_side'|'latch'
 * @param alignY  Vertical alignment: 'top'|'bottom'|'middle'
 * @param alignZ  Depth alignment: 'front'|'back'
 * @param panelW  Panel width (inches)
 * @param panelH  Panel height (inches)
 * @param panelT  Panel thickness (inches)
 * @param isLeftHinged  Whether the door is left-hinged (only relevant for doors)
 * @param isDoor  Whether this panel is a door
 */
export function computeAlignmentBase(
  alignX: string,
  alignY: string,
  alignZ: string,
  panelW: number,
  panelH: number,
  panelT: number,
  isLeftHinged: boolean,
  isDoor: boolean,
): Vec3 {
  let x: number;
  let y: number;
  let z: number;

  // -- Align X --
  // For doors, 'left'/'hinge'/'hinge_side' = hinge side, 'right'/'latch'/'open_side' = handle/open side
  // When left-hinged: hinge=left(0), open=right(panelW)
  // When right-hinged: hinge=right(panelW), open=left(0)
  if (alignX === 'left' || alignX === 'hinge' || alignX === 'hinge_side') {
    x = isDoor && !isLeftHinged ? panelW : 0;
  } else if (alignX === 'right' || alignX === 'latch' || alignX === 'open_side') {
    x = isDoor && !isLeftHinged ? 0 : panelW;
  } else if (alignX === 'middle') {
    x = panelW / 2;
  } else {
    x = panelW / 2;
  }

  // -- Align Y --
  switch (alignY) {
    case 'top':
      y = panelH;
      break;
    case 'bottom':
      y = 0;
      break;
    case 'middle':
      y = panelH / 2;
      break;
    default:
      y = panelH / 2;
      break;
  }

  // -- Align Z --
  switch (alignZ) {
    case 'front':
      z = panelT;
      break;
    case 'back':
      z = 0;
      break;
    default:
      z = panelT;
      break;
  }

  return { x, y, z };
}

// ---------------------------------------------------------------------------
// resolveSurfaceToPanelDims
// ---------------------------------------------------------------------------

/**
 * Map a mount_surface string to panel dimensions derived from the cabinet.
 *
 * Panel thickness constants:
 *   PT = 0.75" for standard carcass/door panels
 *   BT = 0.25" for back panels
 *
 * @param mountSurface  The surface identifier (e.g. "Door Panel", "Side Panel")
 * @param dims          Cabinet dimensions
 * @returns Panel width, height, thickness, and whether it is a door
 */
export function resolveSurfaceToPanelDims(
  mountSurface: string,
  dims: CabinetDimensions,
): PanelDims {
  const PT = DEFAULT_PT;
  const BT = DEFAULT_BT;

  switch (mountSurface) {
    case 'Door Panel': {
      // Door width: split evenly across cabinet face, minus panel thickness insets
      const doorW =
        dims.doorCount > 0
          ? (dims.width - 2 * PT) / dims.doorCount
          : dims.width - 2 * PT;
      return { w: doorW, h: dims.height, t: PT, isDoor: true };
    }

    case 'Drawer Front': {
      const drawerH =
        dims.drawerCount > 0 ? dims.height / dims.drawerCount : dims.height;
      return { w: dims.width - 2 * PT, h: drawerH, t: PT, isDoor: false };
    }

    case 'Bottom Panel':
    case 'Top Panel':
      return { w: dims.width - 2 * PT, h: dims.depth, t: PT, isDoor: false };

    case 'Side Panel':
    case 'Side Panel Left':
    case 'Side Panel Right':
      return { w: dims.depth, h: dims.height, t: PT, isDoor: false };

    case 'Back Panel':
      return { w: dims.width, h: dims.height, t: BT, isDoor: false };

    case 'Fixed Shelf':
      return { w: dims.width - 2 * PT, h: dims.depth, t: PT, isDoor: false };

    default:
      // Generic fallback: full cabinet face
      return { w: dims.width, h: dims.height, t: PT, isDoor: false };
  }
}

// ---------------------------------------------------------------------------
// surfaceTransformToWorld
// ---------------------------------------------------------------------------

/**
 * Transform panel-local coordinates to cabinet-world coordinates based on
 * which surface and face the hardware mounts to.
 *
 * Each surface type has a specific coordinate mapping that accounts for
 * the panel's orientation and position within the cabinet carcass.
 *
 * @param localPos      Position in panel-local coordinates
 * @param mountSurface  Cabinet surface name
 * @param mountFace     Which face of the panel ('front', 'back', 'interior_left', etc.)
 * @param dims          Cabinet dimensions
 * @returns World-space position and rotation
 */
export function surfaceTransformToWorld(
  localPos: Vec3,
  mountSurface: string,
  mountFace: string,
  dims: CabinetDimensions,
): { position: Vec3; rotation: Vec3 } {
  const W = dims.width;
  const H = dims.height;
  const D = dims.depth;
  const PT = DEFAULT_PT;
  const BT = DEFAULT_BT;

  let position: Vec3;
  let rotation: Vec3 = { x: 0, y: 0, z: 0 };

  switch (mountSurface) {
    // ----- Door Panel -----
    // Doors sit in front of the cabinet carcass (Z >= D).
    // Panel X → cabinet X (offset by door left edge), Panel Y → cabinet Y,
    // Panel Z → cabinet Z (offset forward from depth).
    case 'Door Panel': {
      position = {
        x: localPos.x,
        y: localPos.y,
        z: mountFace === 'back'
          ? D + (DEFAULT_PT - localPos.z)
          : D + localPos.z,
      };
      break;
    }

    // ----- Drawer Front -----
    // Similar to doors, drawer fronts are at the cabinet face.
    case 'Drawer Front': {
      position = {
        x: PT + localPos.x,
        y: localPos.y,
        z: D + localPos.z,
      };
      break;
    }

    // ----- Bottom Panel -----
    // Horizontal panel at the bottom of the carcass.
    // Panel X → cabinet X (inset by side panel thickness),
    // Panel Y (along depth) → cabinet Z, Panel Z → cabinet Y (inverted: front=top).
    case 'Bottom Panel': {
      position = {
        x: PT + localPos.x,
        y: PT - localPos.z,
        z: localPos.y,
      };
      rotation = { x: -Math.PI / 2, y: 0, z: 0 };
      break;
    }

    // ----- Top Panel -----
    // Horizontal panel at the top of the carcass.
    // Same X/Z mapping as bottom, but Y is at cabinet height.
    case 'Top Panel': {
      position = {
        x: PT + localPos.x,
        y: H - localPos.z,
        z: localPos.y,
      };
      rotation = { x: Math.PI / 2, y: 0, z: 0 };
      break;
    }

    // ----- Side Panel -----
    // Vertical panel on left or right side.
    // Panel X (along depth) → cabinet Z, Panel Y → cabinet Y,
    // Panel Z → cabinet X (orientation depends on left vs right).
    case 'Side Panel':
    case 'Side Panel Left':
    case 'Side Panel Right': {
      const isRight =
        mountFace === 'interior_right' || mountSurface === 'Side Panel Right';
      if (isRight) {
        position = {
          x: W - PT + (PT - localPos.z),
          y: localPos.y,
          z: localPos.x,
        };
        rotation = { x: 0, y: Math.PI, z: 0 };
      } else {
        // Left panel or interior_left
        position = {
          x: localPos.z,
          y: localPos.y,
          z: localPos.x,
        };
      }
      break;
    }

    // ----- Back Panel -----
    // Vertical panel at the rear of the carcass (Z near 0).
    // Panel X → cabinet X, Panel Y → cabinet Y,
    // Panel Z → cabinet Z (z=0 is exterior, z=BT is interior face).
    case 'Back Panel': {
      position = {
        x: localPos.x,
        y: localPos.y,
        z: localPos.z,
      };
      break;
    }

    // ----- Fixed Shelf -----
    // Horizontal panel inside the carcass, evenly spaced.
    // Same mapping as bottom panel but at a computed Y position.
    case 'Fixed Shelf': {
      position = {
        x: PT + localPos.x,
        y: -localPos.z, // caller adds shelfY offset
        z: localPos.y,
      };
      rotation = { x: -Math.PI / 2, y: 0, z: 0 };
      break;
    }

    default: {
      // Fallback: use local position directly
      position = { ...localPos };
      break;
    }
  }

  return { position, rotation };
}

// ---------------------------------------------------------------------------
// applyMirrorAndPattern
// ---------------------------------------------------------------------------

/**
 * Post-process placements to apply mirror_mode and linear pattern.
 *
 * Mirror creates a reflected clone of each non-mirrored placement;
 * pattern creates evenly-spaced copies along the spacing vector.
 *
 * Mirror modes:
 *   - 'left_right': reflects X across cabinet width center (or custom centerline)
 *   - 'top_bottom': reflects Y across cabinet height center
 *   - 'front_back': reflects Z across cabinet depth center
 *
 * @param placements   Base placements to expand
 * @param mirrorMode   Mirror mode string
 * @param centerline   Mirror centerline override ('cabinet_center' or numeric string)
 * @param patternQty   Number of pattern copies (1 = no pattern)
 * @param spacing      Pattern spacing vector {x, y, z}
 * @param dims         Cabinet dimensions (used for mirror axis)
 * @returns Expanded placement array
 */
export function applyMirrorAndPattern(
  placements: HardwarePlacement[],
  mirrorMode: string,
  centerline: string,
  patternQty: number,
  spacing: Vec3,
  dims: CabinetDimensions,
): HardwarePlacement[] {
  let result = [...placements];

  // -- Mirror: reflected clone of each non-mirrored placement --
  if (mirrorMode !== 'none' && mirrorMode !== '') {
    const mirrored: HardwarePlacement[] = [];

    for (const p of placements) {
      if (p.mirrored) continue; // don't double-mirror already-mirrored placements

      const mp: HardwarePlacement = {
        position: { ...p.position },
        rotation: { ...p.rotation },
        mirrored: true,
        surface: p.surface,
        componentRef: (p.componentRef || '') + ' (M)',
      };

      if (mirrorMode === 'left_right') {
        const mirrorX = parseCenterline(centerline, dims.width / 2);
        mp.position.x = 2 * mirrorX - p.position.x;
        mp.rotation.y = Math.PI - p.rotation.y;
      } else if (mirrorMode === 'top_bottom') {
        const mirrorY = parseCenterline(centerline, dims.height / 2);
        mp.position.y = 2 * mirrorY - p.position.y;
        mp.rotation.x = Math.PI - p.rotation.x;
      } else if (mirrorMode === 'front_back') {
        const mirrorZ = parseCenterline(centerline, dims.depth / 2);
        mp.position.z = 2 * mirrorZ - p.position.z;
        mp.rotation.y = -p.rotation.y;
      }

      mirrored.push(mp);
    }

    result = result.concat(mirrored);
  }

  // -- Linear Pattern: clone all at spacing intervals --
  if (patternQty > 1) {
    const base = [...result];
    for (let i = 1; i < patternQty; i++) {
      for (const p of base) {
        result.push({
          position: {
            x: p.position.x + spacing.x * i,
            y: p.position.y + spacing.y * i,
            z: p.position.z + spacing.z * i,
          },
          rotation: { ...p.rotation },
          mirrored: p.mirrored,
          surface: p.surface,
          componentRef: p.componentRef,
        });
      }
    }
  }

  return result;
}

/**
 * Parse a centerline string into a numeric value.
 * If the string is a valid number, use it directly; otherwise fall back to the default.
 */
function parseCenterline(centerline: string, defaultValue: number): number {
  if (!centerline || centerline === 'cabinet_center') return defaultValue;
  const parsed = parseFloat(centerline);
  return isNaN(parsed) ? defaultValue : parsed;
}

// ---------------------------------------------------------------------------
// computeHardwarePlacements  (main entry point)
// ---------------------------------------------------------------------------

/**
 * Compute all hardware placement positions for a set of mounting profiles
 * on a given cabinet.
 *
 * For each profile this function:
 *   1. Resolves mount_surface to panel dimensions
 *   2. Determines quantity from quantity_rule (fixed, per_door, per_drawer, per_shelf)
 *   3. For each target instance:
 *      - Calls computeAlignmentBase for panel-local position
 *      - Adds profile offsets
 *      - If distribute_enabled, evenly spaces distribute_qty copies along panel height
 *      - Calls surfaceTransformToWorld to get cabinet-space coordinates
 *      - Applies profile rotation
 *   4. Calls applyMirrorAndPattern on all placements from that profile
 *   5. Returns the flattened array of all placements
 *
 * @param profiles  Array of mounting profiles to process
 * @param dims      Cabinet dimensions
 * @returns Array of hardware placements in cabinet-world coordinates
 */
export function computeHardwarePlacements(
  profiles: MountingProfileData[],
  dims: CabinetDimensions,
): HardwarePlacement[] {
  const allPlacements: HardwarePlacement[] = [];

  const W = dims.width;
  const H = dims.height;
  const D = dims.depth;
  const PT = DEFAULT_PT;

  for (const profile of profiles) {
    const { panelType, mountFace } = resolveMountSurface(profile);
    const alignX = profile.align_x || 'middle';
    const alignY = profile.align_y || 'middle';
    const alignZ = profile.align_z || 'front';

    // Quantity rule evaluation
    let qty = profile.quantity_value || 1;
    if (profile.quantity_rule === 'per_door') {
      qty = dims.doorCount || 0;
    } else if (profile.quantity_rule === 'per_drawer') {
      qty = dims.drawerCount || 0;
    } else if (profile.quantity_rule === 'per_shelf') {
      qty = dims.shelfCount || 0;
    }
    // 'fixed' or unrecognized: use quantity_value as-is

    // Profile offsets
    const offX = profile.offset_x || 0;
    const offY = profile.offset_y || 0;
    const offZ = profile.offset_z || 0;

    // Profile rotation (degrees → radians)
    const profRotX = (profile.rotation_x || 0) * DEG2RAD;
    const profRotY = (profile.rotation_y || 0) * DEG2RAD;
    const profRotZ = (profile.rotation_z || 0) * DEG2RAD;

    // Distribute settings
    const distEnabled = profile.distribute_enabled;
    const distQty = distEnabled ? (profile.distribute_qty || 2) : 0;

    // Collect per-profile placements before mirror/pattern
    const profilePlacements: HardwarePlacement[] = [];

    // -----------------------------------------------------------------
    // Door Panel
    // -----------------------------------------------------------------
    if (panelType === 'Door Panel') {
      const numDoors = dims.doorCount || 0;
      if (numDoors <= 0) continue;

      // For the primary door (index 0)
      const doorW = (W - 2 * PT) / numDoors;
      const doorH = H;
      const doorT = PT;
      const isLeftHinged0 = dims.isLeftHinged;
      const door0LeftEdgeX = PT; // first door starts after left side panel

      const base = computeAlignmentBase(
        alignX, alignY, alignZ,
        doorW, doorH, doorT,
        isLeftHinged0, true,
      );

      // Flip offset_x when door hinge side is mirrored (right-hinged)
      const effectiveOffX =
        !isLeftHinged0 && alignX !== 'middle' ? -offX : offX;

      const cabX = door0LeftEdgeX + base.x + effectiveOffX;
      const cabZ =
        mountFace === 'back'
          ? D + (doorT - base.z) + offZ
          : D + base.z + offZ;

      const localY = base.y + offY;
      const yPositions =
        distEnabled && distQty >= 2
          ? computeDistributeYPositions(localY, doorH, distQty)
          : [localY];

      for (const ly of yPositions) {
        profilePlacements.push({
          position: { x: cabX, y: ly, z: cabZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: 'Door 1',
        });

        // For multi-door cabinets: auto-mirror to remaining doors
        // (only when user has not set an explicit mirror_mode)
        const mirrorMode = profile.mirror_mode || 'none';
        if (numDoors > 1 && mirrorMode === 'none') {
          const door0RightEdgeX = PT + doorW;
          const door1LeftEdgeX = door0RightEdgeX; // adjacent doors share edge
          const mirrorCenterX = (door0RightEdgeX + door1LeftEdgeX) / 2;
          const mirroredX = 2 * mirrorCenterX - cabX;

          for (let doorIdx = 1; doorIdx < numDoors; doorIdx++) {
            profilePlacements.push({
              position: { x: mirroredX, y: ly, z: cabZ },
              rotation: { x: profRotX, y: profRotY, z: profRotZ },
              mirrored: true,
              surface: panelType + ':' + mountFace,
              componentRef: `Door ${doorIdx + 1}`,
            });
          }
        }
      }

    // -----------------------------------------------------------------
    // Drawer Front
    // -----------------------------------------------------------------
    } else if (panelType === 'Drawer Front') {
      const numDrawers = dims.drawerCount || 0;
      if (numDrawers <= 0) continue;

      const drawerW = W - 2 * PT;
      const drawerT = PT;

      for (let di = 0; di < numDrawers; di++) {
        const drawerH = H / numDrawers;
        const drawerCenterY = drawerH * (di + 0.5);

        const base = computeAlignmentBase(
          alignX, alignY, alignZ,
          drawerW, drawerH, drawerT,
          false, false,
        );

        const cabX = PT + base.x + offX;
        const cabY = (drawerCenterY - drawerH / 2) + base.y + offY;
        const cabZ = D + base.z + offZ;

        profilePlacements.push({
          position: { x: cabX, y: cabY, z: cabZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: `Drawer ${di + 1}`,
        });
      }

    // -----------------------------------------------------------------
    // Side Panel (interior left / interior right)
    // -----------------------------------------------------------------
    } else if (
      panelType === 'Side Panel' &&
      (mountFace === 'interior_left' || mountFace === 'interior_right')
    ) {
      const isRight = mountFace === 'interior_right';
      const panelW = D;
      const panelH = H;

      for (let i = 0; i < qty; i++) {
        const base = computeAlignmentBase(
          alignX, alignY, alignZ,
          panelW, panelH, PT,
          false, false,
        );

        let cabX: number;
        const cabY = base.y + offY;
        const cabZ = base.x + offZ; // Panel X (along depth) → cabinet Z

        if (isRight) {
          cabX = (W - PT) + (PT - base.z) + offX; // z=front means interior face
        } else {
          cabX = base.z + offX; // z=front means interior face (towards center)
        }

        profilePlacements.push({
          position: { x: cabX, y: cabY, z: cabZ },
          rotation: {
            x: profRotX,
            y: (isRight ? Math.PI : 0) + profRotY,
            z: profRotZ,
          },
          mirrored: isRight,
          surface: panelType + ':' + mountFace,
          componentRef: isRight ? 'Right Panel' : 'Left Panel',
        });
      }

    // -----------------------------------------------------------------
    // Side Panel (exterior)
    // -----------------------------------------------------------------
    } else if (panelType === 'Side Panel' && mountFace === 'exterior') {
      const panelW = D;
      const panelH = H;

      for (let i = 0; i < qty; i++) {
        const base = computeAlignmentBase(
          alignX, alignY, alignZ,
          panelW, panelH, PT,
          false, false,
        );

        const cabX = (PT - base.z) + offX; // z=front is exterior (outward)
        const cabY = base.y + offY;
        const cabZ = base.x + offZ;

        profilePlacements.push({
          position: { x: cabX, y: cabY, z: cabZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: 'Left Panel Exterior',
        });
      }

    // -----------------------------------------------------------------
    // Bottom Panel
    // -----------------------------------------------------------------
    } else if (panelType === 'Bottom Panel') {
      const panelW = W - 2 * PT;
      const panelH = D; // "height" = depth direction for bottom panel

      for (let i = 0; i < qty; i++) {
        const base = computeAlignmentBase(
          alignX, alignY, alignZ,
          panelW, panelH, PT,
          false, false,
        );

        // Panel X → cabinet X (offset by PT), Panel Y → cabinet Z, Panel Z → cabinet Y
        const cabX = PT + base.x + offX;
        const cabZ = base.y + offZ;
        const cabY = (PT - base.z) + offY; // z=front means top face (interior)

        profilePlacements.push({
          position: { x: cabX, y: cabY, z: cabZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: 'Bottom Panel',
        });
      }

    // -----------------------------------------------------------------
    // Top Panel
    // -----------------------------------------------------------------
    } else if (panelType === 'Top Panel') {
      const panelW = W - 2 * PT;
      const panelH = D;

      for (let i = 0; i < qty; i++) {
        const base = computeAlignmentBase(
          alignX, alignY, alignZ,
          panelW, panelH, PT,
          false, false,
        );

        const cabX = PT + base.x + offX;
        const cabZ = base.y + offZ;
        const cabY = H - base.z + offY; // z=front means bottom face (interior)

        profilePlacements.push({
          position: { x: cabX, y: cabY, z: cabZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: 'Top Panel',
        });
      }

    // -----------------------------------------------------------------
    // Back Panel
    // -----------------------------------------------------------------
    } else if (panelType === 'Back Panel') {
      const panelW = W;
      const panelH = H;

      for (let i = 0; i < qty; i++) {
        const base = computeAlignmentBase(
          alignX, alignY, alignZ,
          panelW, panelH, DEFAULT_BT,
          false, false,
        );

        // z=0 is exterior, z=BT is interior face
        const cabX = base.x + offX;
        const cabY = base.y + offY;
        const cabZ = base.z + offZ;

        profilePlacements.push({
          position: { x: cabX, y: cabY, z: cabZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: 'Back Panel',
        });
      }

    // -----------------------------------------------------------------
    // Fixed Shelf
    // -----------------------------------------------------------------
    } else if (panelType === 'Fixed Shelf') {
      const numShelves = dims.shelfCount || 0;
      const panelW = W - 2 * PT;
      const panelH = D;

      for (let si = 0; si < numShelves; si++) {
        const base = computeAlignmentBase(
          alignX, alignY, alignZ,
          panelW, panelH, PT,
          false, false,
        );

        // Distribute shelves evenly across cabinet height
        const shelfSpacing = H / (numShelves + 1);
        const shelfY = shelfSpacing * (si + 1);

        const cabX = PT + base.x + offX;
        const cabZ = base.y + offZ;
        const cabY = shelfY - base.z + offY; // z=front means top face

        profilePlacements.push({
          position: { x: cabX, y: cabY, z: cabZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: `Shelf ${si + 1}`,
        });
      }

    // -----------------------------------------------------------------
    // Generic fallback
    // -----------------------------------------------------------------
    } else {
      for (let i = 0; i < qty; i++) {
        profilePlacements.push({
          position: { x: offX, y: offY, z: offZ },
          rotation: { x: profRotX, y: profRotY, z: profRotZ },
          mirrored: false,
          surface: panelType + ':' + mountFace,
          componentRef: panelType,
        });
      }
    }

    // Apply mirror and linear pattern post-processing for this profile
    const expanded = applyMirrorAndPattern(
      profilePlacements,
      profile.mirror_mode || 'none',
      profile.mirror_centerline || 'cabinet_center',
      profile.pattern_qty || 1,
      {
        x: profile.pattern_spacing_x || 0,
        y: profile.pattern_spacing_y || 0,
        z: profile.pattern_spacing_z || 0,
      },
      dims,
    );

    allPlacements.push(...expanded);
  }

  return allPlacements;
}
